/**
 * Market view-model (spec §7.3, plan §6 M2-18): sell and buy rows per ware, the hold's components and data cores,
 * unit prices through `quote()`, a saturation meter, the "better price at <station> (+x%)" hint from
 * `bestSellStation`, quantity limits for the steppers, and the sell-all estimate. Sealed pods are contract cargo.
 *
 * **Goods** (economy plan SY-2c): while the strategic economy prices the station and `career.json goods.tradeEnabled`
 * is on, a Goods section lists sell rows for the goods in the hold and buy rows for the goods the station sells,
 * priced through the `quoteGood` port. With the flag off (or no quote port) the section is empty and hidden.
 *
 * **Break down** (economy plan SY-3): with the same flag on, a component the docked station can take apart carries a
 * `breakDown` preview (the goods it yields, and whether they fit the hold). With the flag off no row has one.
 *
 * **Deliver** (economy plan SY-7): at a market station of `salvage.json coreDelivery.faction`, a generic data core
 * carries a `deliver` option (hand it over for standing, no credits); disabled when the standing is already at the
 * cap. Story, sealed and contract cores never have one. Not gated by the goods flag.
 *
 * **Read for fragments** (M8 plan MC-h5b, D-516): at a market station of `blueprints.json faction` (the Mutual), a
 * generic data core carries a `read` option (`UiActions.craft` `dataCore.decode`: the core becomes one blueprint
 * fragment); disabled when the fragment archive is at its cap. Story, sealed and contract cores never have one. The
 * player text never says "decode" (M7 Mission 1 owns that word); it lives in `blueprints.json text`.
 *
 * **After-trade price** (M8 plan TR-4): `afterTradeLabel` gives a goods row "Your trade moves the price to X" for the
 * selected quantity, from `quoteGood(...).fillAfter` through `sellUnitAtFill` (no port change).
 *
 * **Rush spares** (M8 plan MC-h4a): no strategic market quotes them, so their goods row reads "Not bought here",
 * except at a buyer (Holdfast) market station, where it carries a `rushSale` and sells whole spares at
 * `base × premium` each through `UiActions.craft` (`rushSpares.sell`). Still gated by the goods flag.
 *
 * **Best sale and margin** (core-loop plan CL-3, Q4): every buy row carries `bestSale`, the other station of this system
 * that pays most per unit right now (ores through `bestSellStation` at qty 0; goods through the read-only
 * `UiActions.quoteGoodAt` port over the system's other stations), its unit price and the margin against this
 * station's ask (`▲ +12%` / `▼ −9%`, a glyph and a sign, never colour alone), or "No buyer in this system". Every
 * quote is what the player pays or gets, so the claim tariff is included (`strategic-link/market.ts` scales the rows
 * and quotes by it), and the label says so. Live prices of this system only (it is covered, D-503); further out is
 * the map's Routes layer. A held good's sell row carries the same "better price at" hint as a ware row.
 *
 * **Sell rows and section order** (CL-3, Q8): ore sell rows appear only for ore held, like goods. `firstSection` is
 * `'sell'` only when something held sells here; else `'buy'`, with `holdSummary` saying where the hold sells
 * ("Your hold: 5.0 t Steel sells at Charter Hub, 293 cr/t"). `wareAfterTradeLabel` gives ore rows the goods' "Your
 * trade moves the price to X" line, through the docked station's read-only `UiActions.quoteWare` port.
 */
import { breakDownComponent, breakDownLabel, breakDownLines } from '../../career/breakdown'
import { deliverCore } from '../../career/cores'
import { BLUEPRINTS, decodeCore, fragmentsOf } from '../../craft/blueprints'
import { RUSH_SPARES, buysRushSparesAt, rushSparesUnit } from '../../career/rushSpares'
import { cargoClassOf, holdMultOf, wareTOfHold } from '../../career/hullFit'
import { EPSILON, GOOD_NAMES, cargoFreeT, contentsLoaded } from '../../career/ops'
import { GOOD_IDS, GOOD_MASS_T, goodT, type GoodId } from '../../career/parts'
import type { CargoComponent, Career } from '../../career/types'
import { moduleStats } from '../../data/m2'
import { WARE_IDS, wareIndex, type M2Data, type StationDef, type WareId } from '../../data/m2Schemas'
import { bestSellStation } from '../../economy/best'
import { satMult } from '../../economy/market'
import { quote, type QuoteItem } from '../../economy/quote'
import { sealedItemRow } from '../../features/storyHud/cargo'
import type { MarketState } from '../../space/contracts'
import { sellUnitAtFill } from '../../strategic/economy/prices'
import { UI_CONFIG, type UiConfig } from '../config'
import type { TenderView, UiActions } from '../contracts'
import { formatCr, formatPct, formatSignedPct, formatTonnes } from '../format'
import type { RowEconomy } from '../strategic/causeModel'
import { rowEconomyAt } from '../strategic/marketRows'
import { deskOf, receiptLine } from './seeds'

export interface PriceHint {
  station: string
  /** Fractional gain over the local price (null when the local station does not buy it). */
  gain: number | null
  label: string
}

export interface WareSellRow {
  ware: WareId
  name: string
  tint: string
  heldT: number
  heldLabel: string
  /** Credits per tonne here (null: not bought here). */
  unit: number | null
  unitLabel: string
  /** 0..1: how far saturation has pushed the price down here (the meter). */
  saturation: number
  saturationLabel: string
  hint: PriceHint | null
  /** Most tonnes the stepper allows: what is held, capped at the buyer's room (PT-1, D-803), when bought here; else 0. */
  maxT: number
  /** PT-2 (D-803): the tonnes this station has room for (only while a strategic station prices the row). */
  roomT?: number
  /** "Room 40 t" (`ui.json market.roomLine`), with `roomT`. */
  roomLabel?: string
  /**
   * PT-2: when the hold holds more than the room, the tonnes that would stay aboard and the offer to store them in the
   * berth here (`berth.store`, M8 MC-b4: free storage at every dockable station).
   */
  storeRest?: { stationId: string; tonnes: number; label: string }
  /** Additive (M4 plan I6): the stock bar, trend and cause chip while the strategic economy prices this station. */
  economy?: RowEconomy
}

/** A unit sale price at another station of this system (CL-3). */
export interface SaleAt {
  stationId: StationDef['id']
  station: string
  /** Credits per unit there (qty 0, standing and tariff included). */
  unit: number
}

/** CL-3: a buy row's best sale in this system and its margin against this station's ask. */
export interface BestSale {
  /** The buying station, or null when no other station of this system buys it. */
  sale: SaleAt | null
  /** `sale.unit / ask − 1` (null with no buyer or no ask). */
  margin: number | null
  /** "▲ +12%", "▼ −9%" or "= 0%"; '' with no margin. */
  marginLabel: string
  /** "Best sale: Charter Hub · 293 cr/t · ▼ −9% (after tariffs)", or "No buyer in this system". */
  label: string
}

export interface WareBuyRow {
  ware: WareId
  name: string
  tint: string
  unit: number
  unitLabel: string
  saturation: number
  saturationLabel: string
  /** Most tonnes that fit in the hold and the wallet. */
  maxT: number
  /** CL-3: where this ore sells best in this system, and the margin. */
  bestSale: BestSale
  /** Additive (M4 plan I6): as on the sell row. */
  economy?: RowEconomy
}

export interface ComponentRow {
  id: string
  name: string
  conditionLabel: string
  massLabel: string
  /** Sale price here (null: not sellable here). */
  price: number | null
  priceLabel: string
  /** "Contract cargo" (sealed pods, recovery targets), "Holds cargo" (a loaded pod), a story item's sealed note (M7 S35), or null. */
  note: string | null
  sellable: boolean
  hint: PriceHint | null
  /** Additive (SY-3): the break-down preview; absent while `goods.tradeEnabled` is off or it can't be broken down here. */
  breakDown?: ComponentBreakDown
  /** Additive (SY-7): the Deliver option of a generic data core at the delivery faction's station; absent elsewhere. */
  deliver?: CoreDeliver
  /** Additive (MC-h5b): reading a generic data core for a blueprint fragment at a Mutual market station; absent elsewhere. */
  read?: CoreRead
}

/** A data core read for a blueprint fragment (MC-h5b): `UiActions.craft({ kind: 'dataCore.decode', stationId, coreId })`. */
export interface CoreRead {
  stationId: StationDef['id']
  /** "Read for blueprint fragments (1 of 6 held)". */
  label: string
  /** False when the fragment archive is at its cap. */
  enabled: boolean
  /** Why it is disabled (the command's own refusal), or null. */
  title: string | null
}

/**
 * The Read option of `c` at `here` (MC-h5b): undefined unless `c` is a generic data core and `here` reads cores
 * (`decodesCoresAt`: a Mutual market station, not refusing the player). Disabled at the fragment cap.
 */
export function readPreview(career: Career, here: StationDef, c: CargoComponent): CoreRead | undefined {
  if (c.type !== 'dataCore') return undefined
  const r = decodeCore(career, here, c.id)
  if (!r.ok && r.error.code !== 'invalidAmount') return undefined
  const label = BLUEPRINTS.text.readButton.replace('{have}', String(fragmentsOf(career))).replace('{cap}', String(BLUEPRINTS.cap))
  return { stationId: here.id, label, enabled: r.ok, title: r.ok ? null : r.error.message }
}

/** A data core's hand-over for standing (SY-7). */
export interface CoreDeliver {
  /** "Deliver to Holdfast for standing". */
  label: string
  /** False when the standing with that faction is already at the cap. */
  enabled: boolean
}

/**
 * The Deliver option of `c` at `here` (SY-7): undefined unless `c` is a generic data core and `here` takes cores
 * (the delivery faction's market station, not refusing the player).
 */
export function deliverPreview(data: M2Data, career: Career, here: StationDef, c: CargoComponent, cfg: UiConfig = UI_CONFIG): CoreDeliver | undefined {
  if (c.type !== 'dataCore') return undefined
  const r = deliverCore(career, data, here, c.id, null)
  if (!r.ok && r.error.code !== 'invalidAmount') return undefined
  return { label: `Deliver to ${cfg.factions[data.salvage.coreDelivery.faction]} for standing`, enabled: r.ok }
}

/** A component's break-down into goods (SY-3). */
export interface ComponentBreakDown {
  /** "3.6 t Electronics, 1.8 t Drive parts". */
  label: string
  /** False when the yields don't fit the hold. */
  enabled: boolean
}

/**
 * The break-down preview of `c` at `here` (SY-3): undefined while the goods flag is off, at a station that doesn't take
 * components apart, or for contract, sealed, story or yield-less components.
 */
export function breakDownPreview(data: M2Data, career: Career, here: StationDef, c: CargoComponent): ComponentBreakDown | undefined {
  const r = breakDownComponent(career, data, here, c.id)
  if (!r.ok && r.error.code !== 'cargoFull') return undefined
  return { label: breakDownLabel(breakDownLines(c)), enabled: r.ok }
}

/** The row-button actions a component row may show (wired actions only get buttons). */
export type RowButtonActions = Pick<UiActions, 'breakDown' | 'deliverCore' | 'craft'>

/** The row fields that carry buttons. */
export type RowButtonFields = Pick<ComponentRow, 'breakDown' | 'deliver' | 'read'>

/** The buttons beside `row` (Break down, then Deliver, then Read; SY-3, SY-7, MC-h5b), counting only wired actions. */
export function rowButtons(row: RowButtonFields, actions: RowButtonActions): number {
  return (
    (actions.breakDown !== undefined && row.breakDown !== undefined ? 1 : 0) +
    (actions.deliverCore !== undefined && row.deliver !== undefined ? 1 : 0) +
    (actions.craft !== undefined && row.read !== undefined ? 1 : 0)
  )
}

/** Row `i`'s first button index in the Market's row-button focus group (`G.breakDown`): the buttons before it. */
export function rowButtonIndex(rows: readonly RowButtonFields[], i: number, actions: RowButtonActions): number {
  let n = 0
  for (let k = 0; k < i; k++) n += rowButtons(rows[k], actions)
  return n
}

/** A good in the hold (SY-2c). Units are `cargo-parts.json massT` tonnes (all 1 t now). */
export interface GoodSellRow {
  good: GoodId
  name: string
  held: number
  heldLabel: string
  /** Credits per unit here (null: not bought here). */
  unit: number | null
  unitLabel: string
  /** Most units the stepper allows (what is held, when bought here; else 0). */
  max: number
  /** CL-3: "Better price at X (+y%)" or "Sells at X" from the `quoteGoodAt` port (null without it, or when here is best). */
  hint: PriceHint | null
  /** Additive (MC-h4a): the Holdfast sale of Rush spares (through `UiActions.craft`, whole units); absent elsewhere. */
  rushSale?: RushSparesSale
}

/** The Rush spares sale at a buyer station (MC-h4a): `max` is the whole spares held, `unit` is `base × premium`. */
export interface RushSparesSale {
  stationId: StationDef['id']
  /** "Sold to the Holdfast at a premium". */
  label: string
  /** "+1 Holdfast standing per spare", or null when the sale gives none. */
  standingLabel: string | null
}

/**
 * The Rush spares goods row at `here` (MC-h4a): sells whole spares at `base × premium` when `here` buys them, else
 * "Not bought here". Null when the hold carries none.
 */
export function rushSparesRow(data: M2Data, career: Career, here: StationDef | undefined, cfg: UiConfig = UI_CONFIG): GoodSellRow | null {
  const held = goodT(career.cargo.parts, 'rush_spares')
  if (!(held > EPSILON)) return null
  const row: GoodSellRow = { good: 'rush_spares', name: GOOD_NAMES.rush_spares, held, heldLabel: `${formatTonnes(held)} t`, unit: null, unitLabel: 'Not bought here', max: 0, hint: null }
  const rs = data.career.rushSpares
  if (rs === undefined || here === undefined || !buysRushSparesAt(here)) return row
  const unit = rushSparesUnit(rs)
  const faction = cfg.factions[RUSH_SPARES.buyer]
  const text = RUSH_SPARES.text
  const rushSale: RushSparesSale = {
    stationId: here.id,
    label: text.rowLabel.replace('{faction}', faction),
    standingLabel: rs.standingGain > 0 ? text.standingLabel.replace('{gain}', String(rs.standingGain)).replace('{faction}', faction) : null,
  }
  return { ...row, unit, unitLabel: `${formatCr(unit)}/t`, max: Math.floor(held + EPSILON), rushSale }
}

/** Stepper step of a goods sell row: whole units for the Rush spares sale (MC-h4a), else the tonnes step. */
export function goodSellStep(row: Pick<GoodSellRow, 'rushSale'>, tonnesStep: number): number {
  return row.rushSale === undefined ? tonnesStep : 1
}

/** A good the station sells (SY-2c). */
export interface GoodBuyRow {
  good: GoodId
  name: string
  unit: number
  unitLabel: string
  /** Units the station has for sale. */
  stock: number
  /** Most units that fit the hold, the wallet (at the marginal price) and the station's stock. */
  max: number
  /** CL-3: where this good sells best in this system (absent without the `quoteGoodAt` port). */
  bestSale?: BestSale
}

/** The quote port the goods rows read (`UiActions.quoteGood`). */
export type GoodQuoter = NonNullable<UiActions['quoteGood']>

/** CL-3: the read-only quote port at another station of this system (`UiActions.quoteGoodAt`). */
export type GoodAtQuoter = NonNullable<UiActions['quoteGoodAt']>

/** CL-3: "▲ +12%" / "▼ −9%" / "= 0%": the glyph follows the shown (rounded) percent, so it never contradicts it. */
export function marginLabel(margin: number, cfg: UiConfig = UI_CONFIG): string {
  const p = Math.round(margin * 100)
  const glyph = p > 0 ? cfg.market.marginUp : p < 0 ? cfg.market.marginDown : cfg.market.marginFlat
  return `${glyph} ${formatSignedPct(margin)}`
}

/** CL-3: the best sale `sale` against this station's ask `ask` (per unit); "No buyer in this system" when null. */
export function bestSaleOf(sale: SaleAt | null, ask: number, cfg: UiConfig = UI_CONFIG): BestSale {
  if (sale === null) return { sale: null, margin: null, marginLabel: '', label: cfg.market.noBuyer }
  const margin = ask > 0 ? sale.unit / ask - 1 : null
  const ml = margin === null ? '' : marginLabel(margin, cfg)
  const label = cfg.market.bestSale.replace('{station}', sale.station).replace('{unit}', `${formatCr(sale.unit)}/t`).replace('{margin}', ml)
  return { sale, margin, marginLabel: ml, label }
}

/** CL-3: the other market station of this system paying most per tonne for `ware` now (qty 0), or null. */
export function bestWareSale(data: M2Data, market: MarketState, career: Career, here: StationDef, ware: WareId): SaleAt | null {
  const best = bestSellStation(data, market, { kind: 'ware', ware }, 0, career.gameTime, career.standings, here.id)
  return best === null ? null : { stationId: best.station.id, station: best.station.name, unit: best.quote.unit }
}

/**
 * CL-3: the station of `stations` other than `hereId` whose `quoteGoodAt` sale unit (qty 0) for `good` is highest
 * (ties keep data order), or null when none buys it. The docked station is never asked.
 */
export function bestGoodSale(stations: readonly StationDef[], hereId: StationDef['id'], quoteGoodAt: GoodAtQuoter, good: GoodId): SaleAt | null {
  let best: SaleAt | null = null
  for (const s of stations) {
    if (s.id === hereId) continue
    const q = quoteGoodAt(s.id, good, 0, 'sell')
    if (q === null) continue
    if (best === null || q.unit > best.unit) best = { stationId: s.id, station: s.name, unit: q.unit }
  }
  return best
}

/** CL-3: a held good's hint, as `priceHint` does for wares (by unit: `local` is this station's sale unit or null). */
export function goodPriceHint(sale: SaleAt | null, local: number | null, minGain: number): PriceHint | null {
  if (sale === null) return null
  if (local === null || local <= 0) return { station: sale.station, gain: null, label: `Sells at ${sale.station}` }
  const gain = sale.unit / local - 1
  if (gain < minGain) return null
  return { station: sale.station, gain, label: `Better price at ${sale.station} (${formatSignedPct(gain)})` }
}

/** CL-3 (d): the docked station's ware quote port (`UiActions.quoteWare`), for the ore rows' after-trade line. */
export type WareQuoter = NonNullable<UiActions['quoteWare']>

/** CL-3 (d): `quote()` at `here` for the career, as the runtime's `quoteWare` port answers (tests and stand-ins). */
export function wareQuoterAt(data: M2Data, market: MarketState, career: Career, here: StationDef): WareQuoter {
  return (ware, qty, side) => quote(data, market, here.id, { kind: 'ware', ware }, qty, side, career.gameTime, career.standings[here.faction])
}

/**
 * CL-3 (d): the unit price of the next tonne of `ware` at the docked station once a trade of `qty` t on `side` has
 * gone through, or null when nothing would trade (no port, zero quantity, not traded here, no room or stock). A
 * strategic quote (it carries `qty`; `satBefore`/`satAfter` are fills) scales by `S(fillAfter) / S(fillBefore)` as
 * goods do (`afterTradeUnit`); M2's formula (no `qty`) scales by `satMult(satAfter) / satMult(satBefore)` with
 * `wares.json market.saturation.k` = `k`.
 */
export function wareAfterTradeUnit(quoteWare: WareQuoter | undefined, k: number, ware: WareId, qty: number, side: 'buy' | 'sell'): number | null {
  if (quoteWare === undefined || !(qty > 0)) return null
  const now = quoteWare(ware, 0, side)
  const q = quoteWare(ware, qty, side)
  if (now === null || q === null) return null
  if (q.qty === undefined) return (now.unit * satMult(q.satAfter, k)) / satMult(q.satBefore, k)
  if (!(q.qty > 0)) return null
  const before = sellUnitAtFill(1, q.satBefore)
  return before > 0 ? (now.unit * sellUnitAtFill(1, q.satAfter)) / before : null
}

/** CL-3 (d): "Your trade moves the price to 412 cr/t" for `qty` t of an ore, or null (as `afterTradeLabel` for goods). */
export function wareAfterTradeLabel(quoteWare: WareQuoter | undefined, k: number, ware: WareId, qty: number, side: 'buy' | 'sell', unitNow: number | null): string | null {
  const after = wareAfterTradeUnit(quoteWare, k, ware, qty, side)
  if (after === null || unitNow === null || formatCr(after) === formatCr(unitNow)) return null
  return `Your trade moves the price to ${formatCr(after)}/t`
}

/**
 * TR-4: the unit price of the next tonne once a trade of `qty` units of `good` on `side` has gone through, or null
 * when nothing would trade (no quote port, not traded here, zero quantity, or no room or stock). The station's curve
 * is linear in fill and every multiplier on it (the fence, standing, the tariff) is a factor, so the after-fill unit is
 * the marginal unit now scaled by `S(fillAfter) / S(fillBefore)` (`sellUnitAtFill`; a buy's markup cancels).
 */
export function afterTradeUnit(quoteGood: GoodQuoter | undefined, good: GoodId, qty: number, side: 'buy' | 'sell'): number | null {
  if (quoteGood === undefined || !(qty > 0)) return null
  const now = quoteGood(good, 0, side)
  const q = quoteGood(good, qty, side)
  if (now === null || q === null || !(q.qty > 0)) return null
  const before = sellUnitAtFill(1, q.fillBefore)
  return before > 0 ? (now.unit * sellUnitAtFill(1, q.fillAfter)) / before : null
}

/**
 * TR-4: "Your trade moves the price to 412 cr/t" for the selected quantity, or null when it would not trade or the
 * price would not move by a shown credit.
 */
export function afterTradeLabel(quoteGood: GoodQuoter | undefined, good: GoodId, qty: number, side: 'buy' | 'sell', unitNow: number | null): string | null {
  const after = afterTradeUnit(quoteGood, good, qty, side)
  if (after === null || unitNow === null || formatCr(after) === formatCr(unitNow)) return null
  return `Your trade moves the price to ${formatCr(after)}/t`
}

/** CL-4: Sell all (and Send tender) sits on top of the Sell section while something aboard sells here. */
export function sellAllOnTop(m: Pick<MarketModel, 'firstSection' | 'canSellAll'>): boolean {
  return m.firstSection === 'sell' && m.canSellAll
}

export interface MarketModel {
  /** CL-3: ore sell rows, for the ore held only (as goods). */
  sell: WareSellRow[]
  buy: WareBuyRow[]
  /** SY-2c: goods in the hold (empty while `goods.tradeEnabled` is off or no strategic market prices the station). */
  goodsSell: GoodSellRow[]
  /** SY-2c: goods the station sells (empty under the same conditions). */
  goodsBuy: GoodBuyRow[]
  components: ComponentRow[]
  /** Estimated credits for "sell all" (every ware and component this station buys). */
  sellAllTotal: number
  sellAllLabel: string
  canSellAll: boolean
  /** M7 S33 (PC-8): the sale receipt footer here ("… · Signed: Maren Voss, Chief Executive"), or null. */
  receipt: string | null
  /** M10 P5a8a: the moored tender strip (only with `moor.json tender.enabled` on; absent today). */
  tender?: TenderView
  /** CL-3 (Q8): Sell first only when something held sells here (a ware or good with room, a sellable component). */
  firstSection: 'buy' | 'sell'
  /** CL-3 (Q8): with Buy first and wares or goods aboard, where they sell ("Your hold: …"); else null. */
  holdSummary: string | null
}

/** The player-facing ware name: tetherstone keeps its unidentified name until an exact (Mk III) scanner is fitted. */
export function wareName(data: M2Data, career: Career, ware: WareId): string {
  const def = data.wares.wares[wareIndex(ware)]
  if (def.unidentifiedName === undefined) return def.name
  return moduleStats(data, 'scanner', career.ship.fitted.scanner).accuracy === 'exact' ? def.name : def.unidentifiedName
}

/** 1 − satMult: the saturation price drop as a 0..1 meter value. */
export function saturationDrop(satBefore: number, k: number): number {
  return 1 - 1 / (1 + k * satBefore)
}

function saturationLabel(drop: number): string {
  return drop < 0.005 ? 'Unsaturated' : `${formatSignedPct(-drop)} saturated`
}

/**
 * The hint for selling `item` elsewhere: the best other station when it pays at least `minGain` more than `local`
 * (or buys it at all when `local` is null or 0).
 *
 * With `qty` 0, `local` is a unit price and stations compare by unit. PT-2 (D-803): with `qty` > 0 (a ware row passes
 * what is held), `local` is the credits here for that quantity (what fits), stations compare by the credits for what
 * fits there, and a station with no room is skipped (`bestSellStation`).
 */
export function priceHint(
  data: M2Data,
  market: MarketState,
  career: Career,
  here: StationDef,
  item: QuoteItem,
  local: number | null,
  minGain: number,
  qty = 0,
): PriceHint | null {
  const best = bestSellStation(data, market, item, qty, career.gameTime, career.standings, here.id)
  if (best === null) return null
  if (local === null || local <= 0) {
    return { station: best.station.name, gain: null, label: `Sells at ${best.station.name}` }
  }
  const gain = (qty > 0 ? best.quote.total : best.quote.unit) / local - 1
  if (gain < minGain) return null
  return { station: best.station.name, gain, label: `Better price at ${best.station.name} (${formatSignedPct(gain)})` }
}

function componentName(data: M2Data, c: CargoComponent): string {
  const def = data.wares.components[data.wares.components.findIndex((d) => d.type === c.type)]
  return def.name
}

/** Rounds a tonnage down to a tenth (so a stepper never offers more than the hold or wallet covers). */
function floorTenth(t: number): number {
  return Math.floor(t * 10 + EPSILON) / 10
}

/** A quote for more than any station holds clamps to its room (PT-2: the "Room N t" line). */
const ROOM_PROBE_T = Number.MAX_SAFE_INTEGER

/** PT-2 (D-803): the room line and, when the hold holds more than fits, the berth offer for the rest. */
function roomFields(stationId: string, roomT: number, held: number, cfg: UiConfig): Pick<WareSellRow, 'roomT' | 'roomLabel' | 'storeRest'> {
  const roomLabel = cfg.market.roomLine.replace('{qty}', formatTonnes(roomT))
  const rest = held - roomT
  if (!(rest > EPSILON)) return { roomT, roomLabel }
  return { roomT, roomLabel, storeRest: { stationId, tonnes: rest, label: cfg.market.storeRest.replace('{qty}', formatTonnes(rest)) } }
}

/** M4 plan I6: `{ economy }` when the strategic economy has a row for the ware here, else nothing (M2's rows unchanged). */
function economyOf(stationId: string, ware: WareId): { economy?: RowEconomy } {
  const economy = rowEconomyAt(stationId, ware)
  return economy === undefined ? {} : { economy }
}

/**
 * The Goods section's rows (SY-2c): empty while `goods.tradeEnabled` is off; else a sell row per good in the hold
 * and a buy row per good the station sells (`quoteGood` answers only under the strategic economy, so M2's own
 * stations show none, and without the port there are none). MC-h4a: Rush spares get their row from
 * `rushSparesRow` (the Holdfast sale at `here`), with or without the port.
 */
export function goodsRows(
  data: M2Data,
  career: Career,
  quoteGood: GoodQuoter | undefined,
  here?: StationDef,
  cfg: UiConfig = UI_CONFIG,
  quoteGoodAt?: GoodAtQuoter,
): { sell: GoodSellRow[]; buy: GoodBuyRow[] } {
  const sell: GoodSellRow[] = []
  const buy: GoodBuyRow[] = []
  if (!data.career.goods.tradeEnabled) return { sell, buy }
  // Rush spares: no strategic quote (career-only); their row comes from the Holdfast sale (or reads 'Not bought here').
  const spares = rushSparesRow(data, career, here, cfg)
  if (quoteGood === undefined) {
    if (spares?.rushSale !== undefined) sell.push(spares)
    return { sell, buy }
  }
  const free = cargoFreeT(career)
  // CL-3: the best sale elsewhere in this system, through the read-only port (none without it or a docked station).
  const saleOf = (good: GoodId): SaleAt | null | undefined =>
    quoteGoodAt === undefined || here === undefined ? undefined : bestGoodSale(data.stations.stations, here.id, quoteGoodAt, good)
  for (const good of GOOD_IDS) {
    if (good === 'rush_spares' && spares !== null) {
      sell.push(spares)
      continue
    }
    const held = goodT(career.cargo.parts, good)
    const sale = saleOf(good)
    if (held > EPSILON) {
      const q = quoteGood(good, 0, 'sell')
      sell.push({
        good,
        name: GOOD_NAMES[good],
        held,
        heldLabel: `${formatTonnes(held)} t`,
        unit: q?.unit ?? null,
        unitLabel: q === null ? 'Not bought here' : `${formatCr(q.unit)}/t`,
        max: q === null ? 0 : held,
        hint: sale === undefined ? null : goodPriceHint(sale, q?.unit ?? null, cfg.hintMinGain),
      })
    }
    // M10 P5a6b H2: the units the free hold carries on the flown hull (× the good's class multiplier; 1× today).
    const fit = (free * holdMultOf(career.ship.defId, cargoClassOf(good))) / GOOD_MASS_T[good]
    const q = quoteGood(good, 0, 'buy')
    if (q === null) continue
    // The stock on sale: a quote for more than any station holds clamps to it.
    const stock = quoteGood(good, Number.MAX_SAFE_INTEGER, 'buy')?.qty ?? 0
    const afford = q.unit > 0 ? career.wallet / q.unit : 0
    buy.push({
      good,
      name: GOOD_NAMES[good],
      unit: q.unit,
      unitLabel: `${formatCr(q.unit)}/t`,
      stock,
      // CL-4 (D-1161 note): goods trade in whole units, so the buy row's max is whole too (every good is 1 t today).
      max: Math.max(0, Math.floor(Math.min(fit, afford, stock) + EPSILON)),
      ...(sale === undefined ? {} : { bestSale: bestSaleOf(sale, q.unit, cfg) }),
    })
  }
  return { sell, buy }
}

export function marketModel(
  data: M2Data,
  career: Career,
  market: MarketState | null,
  here: StationDef,
  cfg: UiConfig = UI_CONFIG,
  quoteGood?: GoodQuoter,
  quoteGoodAt?: GoodAtQuoter,
): MarketModel {
  const m = market ?? {}
  const t = career.gameTime
  const standing = career.standings[here.faction]
  const k = data.wares.market.saturation.k
  const sell: WareSellRow[] = []
  const buy: WareBuyRow[] = []
  let total = 0
  let any = false

  for (const ware of WARE_IDS) {
    const def = data.wares.wares[wareIndex(ware)]
    const item: QuoteItem = { kind: 'ware', ware }
    const held = career.cargo.wares[ware]
    const unitQ = quote(data, m, here.id, item, 0, 'sell', t, standing)
    const hasHeld = held > EPSILON
    // CL-3 (b): a sell row only for ore held (as goods); the buy row's best sale says where it sells before you buy.
    if (hasHeld) {
      const drop = unitQ === null ? 0 : saturationDrop(unitQ.satBefore, k)
      // PT-1/PT-2 (D-803): the room here (a strategic quote clamps to it; M2's formula has none) and the sale of
      // what is held, which sells what fits.
      const heldQ = unitQ !== null && hasHeld ? quote(data, m, here.id, item, held, 'sell', t, standing) : null
      const roomT = unitQ === null ? undefined : quote(data, m, here.id, item, ROOM_PROBE_T, 'sell', t, standing)?.qty
      const room = roomT === undefined ? {} : roomFields(here.id, roomT, held, cfg)
      sell.push({
        ware,
        name: wareName(data, career, ware),
        tint: def.tint,
        heldT: held,
        heldLabel: `${formatTonnes(held)} t`,
        unit: unitQ?.unit ?? null,
        unitLabel: unitQ === null ? 'Not bought here' : `${formatCr(unitQ.unit)}/t`,
        saturation: drop,
        saturationLabel: unitQ === null ? '' : saturationLabel(drop),
        hint: hasHeld ? priceHint(data, m, career, here, item, unitQ === null ? null : (heldQ?.total ?? 0), cfg.hintMinGain, held) : null,
        maxT: unitQ === null ? 0 : roomT !== undefined && roomT < held ? floorTenth(roomT) : held,
        ...room,
        ...economyOf(here.id, ware),
      })
      // Sell-all commits what is held per ware and the worker sells what fits, so the estimate sums what fits.
      if (heldQ !== null) {
        total += heldQ.total
        any = true
      }
    }
    const buyQ = quote(data, m, here.id, item, 0, 'buy', t, standing)
    if (buyQ !== null) {
      const drop = saturationDrop(buyQ.satBefore, k)
      const free = cargoFreeT(career)
      const afford = buyQ.unit > 0 ? career.wallet / buyQ.unit : 0
      buy.push({
        ware,
        name: wareName(data, career, ware),
        tint: def.tint,
        unit: buyQ.unit,
        unitLabel: `${formatCr(buyQ.unit)}/t`,
        saturation: drop,
        saturationLabel: saturationLabel(drop),
        // M10 P5a6b H2: the raw tonnes the free hold carries (wareTOfHold; free itself on every shipped hull).
        maxT: Math.max(0, floorTenth(Math.min(wareTOfHold(career.ship.defId, ware, free), afford))),
        bestSale: bestSaleOf(bestWareSale(data, m, career, here, ware), buyQ.unit, cfg),
        ...economyOf(here.id, ware),
      })
    }
  }

  const components: ComponentRow[] = []
  for (const c of career.cargo.components) {
    // M7 (S35): a story item shows sealed under its own name; nothing quotes, breaks down or delivers it.
    const story = sealedItemRow(c)
    if (story !== null) {
      components.push({ id: c.id, name: story.name, conditionLabel: formatPct(c.condition), massLabel: `${formatTonnes(c.massT)} t`, price: null, priceLabel: story.priceLabel, note: story.note, sellable: false, hint: null })
      continue
    }
    const loaded = c.contents !== undefined && contentsLoaded(c.contents)
    const contract = c.sealed === true || c.contractId !== undefined
    const item: QuoteItem = { kind: 'component', component: c }
    const q = contract || loaded ? null : quote(data, m, here.id, item, 1, 'sell', t, standing)
    const sellable = q !== null
    // Sell all keeps cargo pods aboard (trade.sellAllCargo).
    if (q !== null && c.type !== 'cargoPod') {
      total += q.total
      any = true
    }
    const breakDown = breakDownPreview(data, career, here, c)
    const deliver = deliverPreview(data, career, here, c, cfg)
    const read = readPreview(career, here, c)
    components.push({
      id: c.id,
      name: componentName(data, c),
      conditionLabel: formatPct(c.condition),
      massLabel: `${formatTonnes(c.massT)} t`,
      price: q?.total ?? null,
      priceLabel: q !== null ? formatCr(q.total) : contract ? 'Contract cargo' : loaded ? 'Unload first' : 'Not bought here',
      note: contract ? 'Contract cargo' : loaded ? 'Holds cargo' : null,
      sellable,
      hint: contract || loaded ? null : priceHint(data, m, career, here, item, q === null ? null : q.total, cfg.hintMinGain),
      ...(breakDown === undefined ? {} : { breakDown }),
      ...(deliver === undefined ? {} : { deliver }),
      ...(read === undefined ? {} : { read }),
    })
  }

  const goods = goodsRows(data, career, quoteGood, here, cfg, quoteGoodAt)
  const sellsHere = sell.some((r) => r.maxT > 0) || goods.sell.some((r) => r.max > 0) || components.some((r) => r.sellable)
  const firstSection = sellsHere ? 'sell' : 'buy'
  return {
    firstSection,
    holdSummary: firstSection === 'buy' ? holdSummary(data, m, career, here, sell, goods.sell, quoteGoodAt, cfg) : null,
    sell,
    buy,
    goodsSell: goods.sell,
    goodsBuy: goods.buy,
    components,
    sellAllTotal: total,
    sellAllLabel: any ? `Sell all (≈ ${formatCr(total)})` : 'Sell all',
    canSellAll: any,
    receipt: receiptLine(deskOf(data, here.id)),
  }
}

/**
 * CL-3 (Q8): "Your hold: 5.0 t Steel sells at Charter Hub, 293 cr/t; 2.0 t Ice: no buyer in this system" for the
 * wares and goods held (the first `holdSummaryItems`, then "+N more"), or null when none are. Shown with Buy first.
 * Goods without the `quoteGoodAt` port name no station.
 */
export function holdSummary(
  data: M2Data,
  market: MarketState,
  career: Career,
  here: StationDef,
  wares: readonly WareSellRow[],
  goods: readonly GoodSellRow[],
  quoteGoodAt: GoodAtQuoter | undefined,
  cfg: UiConfig = UI_CONFIG,
): string | null {
  const t = cfg.market
  const items: string[] = []
  const line = (qty: string, name: string, sale: SaleAt | null): string =>
    sale === null
      ? t.holdItemNoBuyer.replace('{qty}', qty).replace('{name}', name)
      : t.holdItemSells.replace('{qty}', qty).replace('{name}', name).replace('{station}', sale.station).replace('{unit}', `${formatCr(sale.unit)}/t`)
  for (const r of wares) items.push(line(r.heldLabel, r.name, bestWareSale(data, market, career, here, r.ware)))
  for (const r of goods) items.push(line(r.heldLabel, r.name, quoteGoodAt === undefined ? null : bestGoodSale(data.stations.stations, here.id, quoteGoodAt, r.good)))
  if (items.length === 0) return null
  const shown = items.slice(0, t.holdSummaryItems)
  const rest = items.length - shown.length
  const joined = rest > 0 ? `${shown.join('; ')}; ${t.holdMore.replace('{n}', String(rest))}` : shown.join('; ')
  return t.holdSummary.replace('{items}', joined)
}
